import React, { useCallback, useMemo } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { Typography } from '@mui/material';
import type {
  GridCellParams,
  GridColDef,
  GridRowParams,
  GridRowProps,
} from '@mui/x-data-grid-premium';
import { DataGridPremium, GridRow, useGridApiRef } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import type { ReportGridRow } from 'src/components';
import {
  AlineCheckbox,
  AlineTooltip,
  allContractTypeFilters,
  allOwnerFilters,
  ContractTypeCell,
  dateFilterOperators,
  dateValueGetter,
  FiltersToolbarButton,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  ReportFiltersPanel,
  SearchInput,
  Spacer,
  StorableIcon,
  useListToolbarStyles,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { useCurrentUserId } from 'src/gql';
import { formatDateForDisplay, getMostPermissiveUserStorableRole } from 'src/helpers';
import {
  createDataGridPremiumPaperStyles,
  evictDocumentsFromReportRowsCache,
  FiltersAnchorProvider,
  removeDocumentsFromGridApi,
  useDataGridSearch,
  useFiltersAnchorState,
  useReportDataSource,
  useStickySelectAllAcrossFetches,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { RepositoryTrashActions } from './RepositoryTrashFooter';
import type { RepositoryView } from './RepositoryViewsDropdown';
import { RepositoryViewsDropdown } from './RepositoryViewsDropdown';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TrashGridDef<CellValue = any, FormattedValue = any> = GridColDef<
  ReportGridRow,
  CellValue,
  FormattedValue
>;

const useStyles = makeStyles()(theme => ({
  emptyIcon: {
    height: 32,
    width: 32,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  rowDisabled: {
    opacity: 0.5,
  },
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  xGrid: {
    flexGrow: 1,
    minHeight: 0,
  },
}));

const TRASH_DEFAULT_SORT_MODEL = [
  { field: 'deletedAt' satisfies keyof ReportGridRow, sort: 'desc' },
] as const;
const FILTERABLE_COLUMNS = 'all' as const;
const FILTER_PANEL_PLACEMENT = 'bottom-start' as const;

interface RepositoryTrashProps {
  repositoryId: UUID;
  onDocumentsRestored?: () => void;
  onRepositoryViewChange?: (view: RepositoryView) => void;
}

export function RepositoryTrash({
  onDocumentsRestored,
  onRepositoryViewChange,
  repositoryId,
}: RepositoryTrashProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiRef();
  const client = useApolloClient();
  const currentUserId = useCurrentUserId();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const {
    clearReportDataSourceCaches,
    dataSource,
    dataSourceCache,
    documentCount,
    filterModelTuple: [filterModel, setFilterModel],
    onDataSourceError,
    removeDocumentsFromDataSource,
    sortModelTuple: [sortModel, setSortModel],
  } = useReportDataSource(repositoryId, {
    apiRef,
    report: { state: { sorting: { sortModel: TRASH_DEFAULT_SORT_MODEL } } },
    trashed: true,
  });

  const removeDocumentsFromGrid = useCallback(
    (documentIds: readonly UUID[]) => {
      evictDocumentsFromReportRowsCache({
        cache: client.cache,
        documentIds,
        reportId: repositoryId,
      });
      removeDocumentsFromGridApi({
        api: apiRef.current,
        dataSourceCache,
        documentIds,
        removeDocumentsFromDataSource,
      });
    },
    [apiRef, client, dataSourceCache, removeDocumentsFromDataSource, repositoryId]
  );

  const refreshGrid = useCallback(() => {
    clearReportDataSourceCaches();
    apiRef.current?.dataSource?.cache?.clear();
    void apiRef.current?.dataSource?.fetchRows();
  }, [apiRef, clearReportDataSourceCaches]);

  const getIsRowDisabled = useCallback(
    (row: ReportGridRow | undefined) => {
      if (!row?.owner) return { isRowDisabled: true, reason: '' };
      const { isEditor, isOwner } = getMostPermissiveUserStorableRole(currentUserId, row);
      const isPermitted = isOwner || isEditor;

      return {
        isRowDisabled: !isPermitted,
        reason: !isPermitted ? 'Not authorized to restore or delete' : '',
      };
    },
    [currentUserId]
  );

  const colDefs = useMemo(
    (): [TrashGridDef, TrashGridDef, TrashGridDef, TrashGridDef] => [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        flex: 2,
        headerName: 'Title',
        renderCell: params => <NameCell {...params} />,
        type: 'string',
      },
      {
        field: 'type',
        filterOperators: allContractTypeFilters,
        flex: 1,
        headerName: 'Type',
        renderCell: ContractTypeCell,
      },
      {
        field: 'owner',
        filterOperators: allOwnerFilters,
        flex: 1,
        headerName: 'Owner',
        renderCell: params => <OwnerCell {...params} />,
      },
      {
        field: 'deletedAt',
        filterOperators: dateFilterOperators,
        flex: 1,
        headerName: 'Deleted At',
        renderCell: ({ value }) => {
          return <Typography>{formatDateForDisplay(value, 'MMM d, yyyy')}</Typography>;
        },
        type: 'dateTime',
        valueGetter: dateValueGetter,
      },
    ],
    []
  );

  const rowExtraProps = useMemo<Pick<TrashRowProps, 'getIsRowDisabled'>>(
    () => ({ getIsRowDisabled }),
    [getIsRowDisabled]
  );
  const paginationModel = useMemo(() => ({ page: 0, pageSize: 50 }), []);
  const getRowClassName = useCallback(
    (params: GridRowParams<ReportGridRow>) => {
      const { isRowDisabled } = getIsRowDisabled(params.row);
      return isRowDisabled ? classes.rowDisabled : '';
    },
    [classes.rowDisabled, getIsRowDisabled]
  );
  const isRowSelectable = useCallback(
    ({ row }: GridRowParams<ReportGridRow>) => {
      const { isRowDisabled } = getIsRowDisabled(row);
      return !isRowDisabled;
    },
    [getIsRowDisabled]
  );
  const rowSlot = useCallback(
    (props: GridRowProps) => <TrashRow {...props} getIsRowDisabled={getIsRowDisabled} />,
    [getIsRowDisabled]
  );
  const slotProps = useMemo(
    () => ({
      filterPanel: {
        filterableColumns: FILTERABLE_COLUMNS,
      },
      footer: {
        noun: 'documents',
        totalCount: documentCount ?? 0,
      },
      panel: {
        placement: FILTER_PANEL_PLACEMENT,
        target: filtersAnchorEl ?? undefined,
      },
      row: rowExtraProps,
      toolbar: {
        onRepositoryViewChange,
      },
    }),
    [documentCount, filtersAnchorEl, onRepositoryViewChange, rowExtraProps]
  );
  const slots = useMemo(
    () => ({
      baseCheckbox: AlineCheckbox,
      baseTooltip: GridAlineTooltip,
      filterPanel: ReportFiltersPanel,
      footer: ListCountFooter,
      noRowsOverlay: TrashEmpty,
      row: rowSlot,
      toolbar: TrashToolbar,
    }),
    [rowSlot]
  );

  useStickySelectAllAcrossFetches(apiRef);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.listContainer}>
        <DataGridPremium
          checkboxSelection
          checkboxSelectionVisibleOnly
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          lazyLoading
          pagination
          showToolbar
          apiRef={apiRef}
          className={classes.xGrid}
          columns={colDefs}
          dataSource={dataSource}
          dataSourceCache={dataSourceCache}
          filterModel={filterModel}
          getRowClassName={getRowClassName}
          isRowSelectable={isRowSelectable}
          paginationModel={paginationModel}
          rowCount={documentCount}
          slotProps={slotProps}
          slots={slots}
          sortModel={sortModel}
          sx={createDataGridPremiumPaperStyles}
          onDataSourceError={onDataSourceError}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
        />
        <RepositoryTrashActions
          apiRef={apiRef}
          documentCount={documentCount ?? 0}
          repositoryId={repositoryId}
          onDocumentsRestored={onDocumentsRestored}
          onRefreshGrid={refreshGrid}
          onRemoveDocumentsFromGrid={removeDocumentsFromGrid}
        />
      </div>
    </FiltersAnchorProvider>
  );
}

type GetIsRowDisabled = (row: ReportGridRow) => {
  isRowDisabled: boolean;
  reason: string;
};

interface TrashRowProps extends GridRowProps {
  getIsRowDisabled?: GetIsRowDisabled;
}

function TrashRow(props: TrashRowProps): JSX.Element {
  const { getIsRowDisabled, row, ...rest } = props;
  const { isRowDisabled = false, reason = '' } =
    row && getIsRowDisabled ? getIsRowDisabled(row as ReportGridRow) : {};

  return (
    <AlineTooltip arrow placement="bottom" title={isRowDisabled ? reason : ''}>
      <div>
        <GridRow row={row} {...rest} />
      </div>
    </AlineTooltip>
  );
}

function TrashEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<DocumentIcon className={classes.emptyIcon} fill="#9CA7AB" />}
      subtitle="Any documents you delete show up here."
      title="No documents"
    />
  );
}

interface TrashToolbarProps {
  onRepositoryViewChange?: (view: RepositoryView) => void;
}

function TrashToolbar({ onRepositoryViewChange }: TrashToolbarProps): JSX.Element {
  const { classes: toolbarClasses } = useListToolbarStyles();
  const { classes } = useStyles();
  const { search, setSearch } = useDataGridSearch();

  return (
    <div className={toolbarClasses.toolbar}>
      <Typography className={classes.title} variant="h6">
        Documents
      </Typography>
      <FiltersToolbarButton />
      {onRepositoryViewChange && (
        <RepositoryViewsDropdown activeView="trash" onViewChange={onRepositoryViewChange} />
      )}
      <Spacer />
      <SearchInput
        aria-label="Search trash"
        placeholder="Search trash..."
        search={search}
        setSearch={setSearch}
      />
    </div>
  );
}

const useOwnerStyles = makeStyles()(theme => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
  },
  title: {
    marginLeft: theme.spacing(1),
  },
}));

function OwnerCell({
  value,
}: GridCellParams<ReportGridRow, ReportGridRow['owner'], string>): JSX.Element {
  const { classes } = useOwnerStyles();
  const currentUserId = useCurrentUserId();
  const isOwnerCurrentUser = value?.id === currentUserId;
  const userName = isOwnerCurrentUser ? 'me' : value?.displayName;

  return (
    <div className={classes.cell}>
      <Typography className={clsx(!isOwnerCurrentUser && classes.title)} variant="body1">
        {userName}
      </Typography>
    </div>
  );
}

const useNameStyles = makeStyles()(theme => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
  },
  title: {
    marginLeft: theme.spacing(1),
  },
}));

function NameCell({
  row,
  value: documentName,
}: GridCellParams<ReportGridRow, ReportGridRow['name'], string>): JSX.Element {
  const { classes } = useNameStyles();

  return (
    <div className={classes.cell}>
      <StorableIcon storable={row} />
      <Typography className={classes.title} variant="body1">
        {documentName}
      </Typography>
    </div>
  );
}
